/* eslint-disable no-async-promise-executor */
import React from 'react';
import moment from 'moment';
// import { isBrowser } from 'react-device-detect';

import { ReactComponent as BreadCrumbIcon } from '../../assets/icons/breadcrumb-icon.svg';

import { fetchCall } from '../../services/api';
import { store } from '../../redux/store';

import {
  SummarySales,
  ModalFilter,
  GraphData,
  GraphDataObject,
  GraphTableData,
  GraphTableDataObject,
  Segment,
  SummaryMedia,
  SummaryShare,
  Graph,
  GraphTable,
  SummaryDCP,
  SummaryNetSales,
  DateRange
} from '../../types';
import { returnCapitalizedString, returnFormattedSegmentFilters } from '../../helpers/format';
import { OktaAuth } from '@okta/okta-auth-js';
import { tokenHasExpired } from '../../helpers/auth';

/**
 * Calculates grid-template-column value based on
 * total elements in array argument
 * @param segments
 * @returns {Array<string,number>} returns array of two values for proper segment styling [grid-template-column, nth-child]
 */
export const calculateSegmentGrid = (): [string, number] => {
  const path = location.pathname.split('/')[2];
  let length;

  switch (path) {
    case 'sales':
      length = 5;
      break;
    case 'media':
      length = 4;
      break;
    case 'share':
      length = 4;
      break;
    case 'digital-commerce-penetration':
      length = 1;
      break;
    default:
      length = 1;
      break;
  }
  // TODO: implement when designs become clearer
  // if browser resolution, force all segments onto single view
  // if (isBrowser) {
  //   return [`repeat(${length}, 1fr)`, length];
  // }

  if (length >= 5 || length % 3 === 0) {
    return ['repeat(3, 1fr)', 3];
  }

  if (length % 2 === 0) {
    return ['repeat(2, 1fr)', 2];
  }

  return ['repeat(2, 1fr)', 1];
};

/**
 * Returns properly formatted BreadCrumb UI
 * @returns React.ReactElement
 */
export const handleCrumbRendering = (): React.ReactElement => {
  const pathArray = location.pathname.split('/');

  const crumbTitle = pathArray[2];

  const crumb = crumbTitle
    .split('-')
    .map((item) => returnCapitalizedString(item))
    .join(' ');

  return (
    <span className="flex items-center gap-x-3 text-[#898989] font-nunitoRegular">
      {' '}
      <BreadCrumbIcon className="my-0 mx-[5px] mb-1 relative top-[2px] fill-current text-[#898989]" /> {crumb}
    </span>
  );
};

export const returnParams = (
  marketFilters: Array<ModalFilter | null>,
  dateFilters: string,
  dateFilterRangeLimit: DateRange[],
  grouping?: string | null
): any => {
  const segmentFilters = returnFormattedSegmentFilters(marketFilters, true);
  const formattedDates = dateFilters.split(' - ');
  const params = {
    ...segmentFilters,
    // sets date to ISO format YYYY-MM-DD (refer to OpenAPI specs)
    startDate: moment(new Date(formattedDates[0])).format('YYYY-MM-DD'),
    endDate: moment(new Date(formattedDates[1])).format('YYYY-MM-DD'),
    grouping,
    maxAvailableDate: dateFilterRangeLimit[0].endDate
  };

  const path = location.pathname.split('/')[2].replace(/-/gi, '').toString();
  return { params, path };
};

export async function fetchEndpoint(
  endpoint: string,
  params: unknown,
  title: string,
  oktaAuth?: OktaAuth
): Promise<any> {
  const api = store.getState().config.react_app_api_base_url;
  return new Promise(async (resolve, reject) => {
    try {
      oktaAuth?.tokenManager.get('idToken').then((idToken) => {
        if (Date.now() + 3600 * 1000 - idToken.expiresAt * 1000 > 60000) {
          tokenHasExpired(200, oktaAuth);
        }
      });
      resolve(await fetchCall(`${api}${endpoint}`, params));
    } catch (error) {
      console.error(error);
      if ((error as any)?.response.status == 401 && oktaAuth)
        tokenHasExpired((error as any)?.response.status, oktaAuth);
      reject(`Error fetching ${title || 'some'} data. Please try again.`);
    }
  });
}

// helpers
const returnGraphObject = (data: Graph): GraphData[] => {
  return data?.groupedDetail[0]?.values;
};
const returnTableObject = (data: GraphTable): GraphTableData[] => {
  return data?.groupedDetail[0]?.values;
};

// ____  Segments
export const returnSalesSegments = (id: string, data: SummarySales): Segment[] => {
  if (id === 'netSales') {
    return generateNetSalesSegment(data);
  } else {
    return generateSalesSegments(data);
  }
};

export const generateNetSalesSegment = (data: SummarySales): Segment[] => {
  const netSales: SummaryNetSales = {
    netSales: data
  };

  return [
    {
      title: 'Net Sales',
      currencyCode: netSales?.netSales.currencyCode,
      currentValue: netSales?.netSales.currentValue,
      currentValueType: netSales?.netSales.currentValueType,
      targetPercentage: netSales?.netSales.planPercentageChange,
      yoyPercentage: netSales?.netSales.yoyPercentageChange,
      momPercentage: netSales?.netSales.momPercentageChange,
      index: 0,
      displayData: ['MoM', 'YoY', 'target']
    }
  ];
};

export const generateSalesSegments = (data: SummarySales): Segment[] => {
  const { conversionRate, orderedRevenue, asp, glanceViews }: SummarySales = data;

  return [
    {
      title: 'POS',
      currencyCode: orderedRevenue?.currencyCode,
      currentValue: orderedRevenue?.currentValue,
      currentValueType: orderedRevenue?.currentValueType,
      targetPercentage: orderedRevenue?.targetPercentage,
      yoyPercentage: orderedRevenue?.yoyPercentage,
      ytdPercentage: orderedRevenue?.ytdPercentage,
      momPercentage: orderedRevenue?.momPercentage,
      index: 1,
      displayData: ['MoM', 'YoY', 'YtD'],
      sectionDetailsTooltip: true
    },
    {
      title: 'Glance Views',
      currencyCode: glanceViews?.currencyCode,
      currentValue: glanceViews?.currentValue,
      currentValueType: glanceViews?.currentValueType,
      targetPercentage: glanceViews?.targetPercentage,
      yoyPercentage: glanceViews?.yoyPercentage,
      ytdPercentage: glanceViews?.ytdPercentage,
      momPercentage: glanceViews?.momPercentage,
      index: 2,
      displayData: ['MoM', 'YoY', 'YtD']
    },
    {
      title: 'Conversion Rate',
      currencyCode: conversionRate?.currencyCode,
      currentValue: conversionRate?.currentValue,
      currentValueType: conversionRate?.currentValueType,
      targetPercentage: conversionRate?.targetPercentage,
      yoyPercentage: conversionRate?.yoyPercentage,
      ytdPercentage: conversionRate?.ytdPercentage,
      momPercentage: conversionRate?.momPercentage,
      index: 3,
      displayData: ['MoM', 'YoY', 'YtD']
    },
    {
      title: 'ASP',
      currencyCode: asp?.currencyCode,
      currentValue: asp?.currentValue,
      currentValueType: asp?.currentValueType,
      targetPercentage: asp?.targetPercentage,
      yoyPercentage: asp?.yoyPercentage,
      ytdPercentage: asp?.ytdPercentage,
      momPercentage: asp?.momPercentage,
      index: 4,
      displayData: ['MoM', 'YoY', 'YtD']
    }
  ];
};
export const returnShareSegment = (id: string, data: SummaryShare): Segment[] => {
  if (id === 'amazonShare') {
    return generateAmazonShareSegment(data);
  }
  if (id === 'competitiveShare') {
    return generateCompetitiveShareSegment(data);
  }
  return [];
};

export const returnMediaSegment = (id: string, data: SummaryMedia): Segment[] => {
  const { amazonSummary }: SummaryMedia = data;
  let [clicksSegment, conversionsSegment, cpaSegment, aovSegment]: Segment[] = [];

  if (amazonSummary?.clicks !== undefined) {
    clicksSegment = {
      title: 'Clicks',
      currencyCode: amazonSummary?.clicks?.currencyCode,
      currentValue: amazonSummary?.clicks?.currentValue,
      currentValueType: amazonSummary?.clicks?.currentValueType,
      targetPercentage: amazonSummary?.clicks?.targetPercentage,
      yoyPercentage: amazonSummary?.clicks?.yoyPercentage,
      ytdPercentage: amazonSummary?.clicks?.ytdPercentage,
      momPercentage: amazonSummary?.clicks?.momPercentage,
      index: 1,
      displayData: ['MoM', 'YoY', 'YtD']
    };
  }
  if (amazonSummary?.conversions !== undefined) {
    conversionsSegment = {
      title: 'Conversions',
      currencyCode: amazonSummary?.conversions?.currencyCode,
      currentValue: amazonSummary?.conversions?.currentValue,
      currentValueType: amazonSummary?.conversions?.currentValueType,
      targetPercentage: amazonSummary?.conversions?.targetPercentage,
      yoyPercentage: amazonSummary?.conversions?.yoyPercentage,
      ytdPercentage: amazonSummary?.conversions?.ytdPercentage,
      momPercentage: amazonSummary?.conversions?.momPercentage,
      index: 2,
      displayData: ['MoM', 'YoY', 'YtD']
    };
  }
  if (amazonSummary?.cpa !== undefined) {
    cpaSegment = {
      title: 'CPA',
      currencyCode: amazonSummary?.cpa?.currencyCode,
      currentValue: amazonSummary?.cpa?.currentValue,
      currentValueType: amazonSummary?.cpa?.currentValueType,
      targetPercentage: amazonSummary?.cpa?.targetPercentage,
      yoyPercentage: amazonSummary?.cpa?.yoyPercentage,
      ytdPercentage: amazonSummary?.cpa?.ytdPercentage,
      momPercentage: amazonSummary?.cpa?.momPercentage,
      index: 3,
      displayData: ['MoM', 'YoY', 'YtD']
    };
  }
  if (amazonSummary?.aov !== undefined) {
    aovSegment = {
      title: 'AOV',
      currencyCode: amazonSummary?.aov?.currencyCode,
      currentValue: amazonSummary?.aov?.currentValue,
      currentValueType: amazonSummary?.aov?.currentValueType,
      targetPercentage: amazonSummary?.aov?.targetPercentage,
      yoyPercentage: amazonSummary?.aov?.yoyPercentage,
      ytdPercentage: amazonSummary?.aov?.ytdPercentage,
      momPercentage: amazonSummary?.aov?.momPercentage,
      index: 4,
      displayData: ['MoM', 'YoY', 'YtD'],
      sectionDetailsTooltip: true
    };
  }

  return [clicksSegment, conversionsSegment, cpaSegment, aovSegment];
};

export const returnDCPSegment = (id: string, data: SummaryDCP): Segment[] => {
  const { penetration }: SummaryDCP = data;
  return [
    {
      title: '% of Total Sales from Digital Commerce',
      currentValue: penetration?.currentValue,
      currentValueType: 'percentage',
      momPercentage: penetration?.momPercentage,
      yoyPercentage: penetration?.yoyPercentage,
      index: 0,
      displayData: ['MoM', 'YoY'],
      sectionDetailsTooltip: true
    }
  ];
};
// Segment generations
export const generateAmazonShareSegment = (data: SummaryShare): Segment[] => [
  {
    title: 'Amazon',
    currencyCode: data.amazonShare?.currencyCode,
    currentValue: data.amazonShare?.currentValue,
    currentValueType: data.amazonShare?.currentValueType,
    l13wPercentage: data.amazonShare?.l13wPercentage,
    l52wPercentage: data.amazonShare?.l52wPercentage,
    momPercentage: data.amazonShare?.momPercentage,
    index: 0,
    displayData: ['MoM', 'last13', 'last52']
  }
];
export const generateCompetitiveShareSegment = (data: SummaryShare): Segment[] => [
  {
    title: 'Competitive <br/> Sales',
    currencyCode: data.amazonShare?.currencyCode,
    currentValue: data.amazonShare?.currentValue,
    currentValueType: data.amazonShare?.currentValueType,
    l13wPercentage: data.amazonShare?.l13wPercentage,
    l52wPercentage: data.amazonShare?.l52wPercentage,
    momPercentage: data.amazonShare.momPercentage,
    index: 1,
    displayData: ['MoM', 'last13', 'last52']
  }
];

// ____ Graphs
export const returnSalesGraph = (id: string, data: Graph): GraphDataObject[] => {
  if (id === 'netSales') {
    return [{ title: 'Net Sales', index: 0, data: returnGraphObject(data), showPlanData: true }];
  }
  if (id === 'POS') {
    return [{ title: 'POS', index: 1, data: returnGraphObject(data) }];
  }
  if (id === 'glanceViewsSales') {
    return [{ title: 'Glance Views', index: 2, data: returnGraphObject(data) }];
  }
  if (id === 'conversionRate') {
    return [{ title: 'Conversion Rate', index: 3, data: returnGraphObject(data) }];
  }
  if (id === 'ASP') {
    return [{ title: 'ASP', index: 4, data: returnGraphObject(data) }];
  }
  return [];
};
export const returnShareGraph = (id: string, data: Graph): GraphDataObject[] => {
  if (id === 'amazonShare') {
    return [{ title: 'Amazon', index: 0, data: returnGraphObject(data) }];
  }
  if (id === 'competitiveShare') {
    return [{ title: 'Competitive <br/> Sales', index: 1, data: returnGraphObject(data) }];
  }
  return [];
};
export const returnMediaGraph = (id: string, data: Graph): GraphDataObject[] => {
  if (id === 'mediaConversions') {
    return [{ title: 'Conversions', index: 2, data: returnGraphObject(data) }];
  }
  if (id === 'mediaClicks') {
    return [{ title: 'Clicks', index: 1, data: returnGraphObject(data) }];
  }
  if (id === 'mediaSPGR') {
    return [{ title: 'Spend As % of Gross <br/> Revenue', index: 0, data: returnGraphObject(data) }];
  }
  if (id === 'mediaCPA') {
    return [{ title: 'CPA', index: 3, data: returnGraphObject(data) }];
  }
  if (id === 'mediaAOV') {
    return [{ title: 'AOV', index: 4, data: returnGraphObject(data) }];
  }
  return [];
};
export const returnDCPGraph = (id: string, data: Graph): GraphDataObject[] => {
  if (id === 'dcp') {
    return [{ title: '% of Total Sales from Digital Commerce', index: 0, data: returnGraphObject(data) }];
  }
  return [];
};

// ___ Tables
export const returnSalesTable = (id: string, data: GraphTable): GraphTableDataObject[] => {
  if (id === 'netSales') {
    return [{ title: 'Net Sales', index: 0, data: returnTableObject(data) }];
  }
  if (id === 'POS') {
    return [{ title: 'POS', index: 1, data: returnTableObject(data) }];
  }
  if (id === 'glanceViewsSales') {
    return [{ title: 'Glance Views', index: 2, data: returnTableObject(data) }];
  }
  if (id === 'conversionRate') {
    return [{ title: 'Conversion Rate', index: 3, data: returnTableObject(data) }];
  }
  if (id === 'ASP') {
    return [{ title: 'ASP', index: 4, data: returnTableObject(data) }];
  }
  return [];
};
export const returnShareTable = (id: string, data: GraphTable): GraphTableDataObject[] => {
  if (id === 'amazonShare') {
    return [{ title: 'Amazon', index: 0, data: returnTableObject(data) }];
  }
  if (id === 'competitiveShare') {
    return [{ title: 'Competitive <br/> Sales', index: 1, data: returnTableObject(data) }];
  }
  return [];
};
export const returnMediaTable = (id: string, data: GraphTable): GraphTableDataObject[] => {
  if (id === 'mediaConversions') {
    return [{ title: 'Conversions', index: 2, data: returnTableObject(data) }];
  }
  if (id === 'mediaClicks') {
    return [{ title: 'Clicks', index: 1, data: returnTableObject(data) }];
  }
  if (id === 'mediaSPGR') {
    return [{ title: 'Spend As % of Gross <br/> Revenue', index: 0, data: returnTableObject(data) }];
  }
  if (id === 'mediaCPA') {
    return [{ title: 'CPA', index: 3, data: returnTableObject(data) }];
  }
  if (id === 'mediaAOV') {
    return [{ title: 'AOV', index: 4, data: returnTableObject(data) }];
  }
  return [];
};
export const returnDCPTable = (id: string, data: GraphTable): GraphTableDataObject[] => {
  if (id === 'dcp') {
    return [
      { title: '% of Total Sales from Digital Commerce', index: 0, data: returnTableObject(data), limitedOptions: true }
    ];
  }
  return [];
};

// const aggregateDCPSegments = (data: any): Segment[] => {
//   const { salesFromDigitalCommerce }: SummaryDCP = data

//   return [
//     {
//       title: '% of Total Sales From <br/> Digital Commerce',
//       currencyCode: salesFromDigitalCommerce?.currencyCode,
//       currentValue: salesFromDigitalCommerce?.currentValue,
//       l13wPercentage: salesFromDigitalCommerce?.l13wPercentage,
//       l52wPercentage: salesFromDigitalCommerce?.l52wPercentage,
//       momPercentage: salesFromDigitalCommerce?.momPercentage,
//       unit: '%',
//       index: 0
//     }
//   ];
// };

// const aggregateMediaSegments = (data: any): Segment[] => {
//   const { spgr, amazonSummary }: SummaryMedia = data
//   return [
//     {
//       title: 'Spend As % of Gross <br/> Revenue',
//       currencyCode: spgr?.currencyCode,
//       currentValue: spgr?.currentValue,
//       targetPercentage: spgr?.targetPercentage,
//       yoyPercentage: spgr?.yoyPercentage,
//       ytdPercentage: spgr?.ytdPercentage,
//       unit: '%',
//       index: 0
//     },
//     {
//       title: 'Clicks',
//       currencyCode: amazonSummary?.clicks?.currencyCode,
//       currentValue: amazonSummary?.clicks?.currentValue,
//       targetPercentage: amazonSummary?.clicks?.targetPercentage,
//       yoyPercentage: amazonSummary?.clicks?.yoyPercentage,
//       ytdPercentage: amazonSummary?.clicks?.ytdPercentage,
//       index: 1
//     },
//     {
//       title: 'Conversions',
//       currencyCode: amazonSummary?.conversions.currencyCode,
//       currentValue: amazonSummary?.conversions.currentValue,
//       targetPercentage: amazonSummary?.conversions.targetPercentage,
//       yoyPercentage: amazonSummary?.conversions.yoyPercentage,
//       ytdPercentage: amazonSummary?.conversions.ytdPercentage,
//       index: 2
//     },
//     {
//       title: 'CPA',
//       currencyCode: amazonSummary?.cpa.currencyCode,
//       currentValue: amazonSummary?.cpa.currentValue,
//       targetPercentage: amazonSummary?.cpa.targetPercentage,
//       yoyPercentage: amazonSummary?.cpa.yoyPercentage,
//       ytdPercentage: amazonSummary?.cpa.ytdPercentage,
//       index: 3
//     }
//   ];
// };

export const numberWithCommas = (number: number | undefined, decimalPlace = 2): string => {
  if (!number) return '';
  return number
    .toFixed(decimalPlace)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

export const hasAllUndefinedProperties = (object: any): boolean => {
  return Object.values(object).every((x) => x === undefined);
};
